import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, filter, map, tap, switchMap, takeUntil } from 'rxjs';
import { ChangeLanguageService } from './change-language.service';

/**
 * Should be provided at the component level, because it needs to access to the router.
 * It will reload the page when the language is changed, because the shop might be already loaded in the browser with product translations.
 * For now the system does not support changing the language without reloading the page.
 */
@Injectable()
export class ChangeLangHandlerService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly changeLanguageService = inject(ChangeLanguageService);

  public readonly changeLang$ = new Subject<string>();

  private readonly routeNavigationEnd$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(event => event as NavigationEnd),
  );

  private readonly changeLangHandler$ = this.changeLang$.pipe(
    tap((lang: string) => {
      this.changeLanguageService.changeLanguage(lang);
    }),
    switchMap(() => this.routeNavigationEnd$),
    tap(() => {
      window.location.reload();
    }),
  );

  private readonly destroy$ = new Subject<void>();
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor() {
    this.changeLangHandler$.pipe(takeUntil(this.destroy$)).subscribe();
  }
}
